import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/Header/Header';
import PublicListPromoBar from '../../components/PublicListPromoBar';
import Footer from '../../components/Footer';
import TabBar from '../../components/Common/TabBar';
import { useAuth } from '../../contexts/AuthContext';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getHeaderTextSizeClass, getSmallTextSizeClass, getStatisticLabelSizeClass, getTextSizeClass } from '../../utils/fontSizeUtils';
import { LockIcon } from '../../components/StockTable';
import { publicListApi } from '../../services/publicListApi';
import { tickerApi } from '../../services/tickerApi';
import { userTickerApi } from '../../services/userTickerApi';
import {
  PROS_CONS_STATISTICS,
  prosConsLineArrays,
  ProsConsNumberedList,
  getProsConsIndicatorsColumnWidthPx,
  getProsConsHistoryMonthColumnWidthPx,
} from '../../utils/prosConsTabUtils';
import { isEtfOrEtvMarketType } from '../../utils/insiderTransactionsTabUtils';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { loadUserTickerPreferences, saveUserTickerPreferences } from '../../utils/tickerPreferencesUtils';

type HistoryItem = {
  year: number | null;
  month: number | null;
  prosCons: {
    pro_1: string | null;
    pro_2: string | null;
    pro_3: string | null;
    pro_4: string | null;
    pro_5: string | null;
    con_1: string | null;
    con_2: string | null;
    con_3: string | null;
    con_4: string | null;
    con_5: string | null;
  } | null;
};

const MONTH_ABBR_DOT = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.'];

function formatMonthYear(year: number | null, month: number | null): string {
  if (!year || !month || month < 1 || month > 12) return '-';
  return `${MONTH_ABBR_DOT[month - 1]} ${year}`;
}

const TickerProsCons: React.FC = () => {
  const { ticker } = useParams<{ ticker: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { fontSize } = useFontSize();

  const searchParams = new URLSearchParams(location.search);
  const publicUuid = searchParams.get('public_uuid');
  const isPublicList = !!publicUuid;

  const [publicListData, setPublicListData] = useState<{ ownerHasProSubscription: boolean } | null>(null);
  useEffect(() => {
    if (publicUuid) {
      publicListApi.getPublicList(publicUuid)
        .then((resp) => setPublicListData({ ownerHasProSubscription: resp.data.owner_has_pro_subscription }))
        .catch(() => setPublicListData({ ownerHasProSubscription: false }));
    } else {
      setPublicListData(null);
    }
  }, [publicUuid]);

  const effectiveHasPro = useMemo(() => {
    if (isPublicList && publicListData) return publicListData.ownerHasProSubscription;
    return Boolean(isAuthenticated && user?.hasProSubscription);
  }, [isPublicList, publicListData, isAuthenticated, user?.hasProSubscription]);

  useEffect(() => {
    document.title = `Stock Table - ${ticker || ''} Pros & Cons`;
  }, [ticker]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tickerBasicsId, setTickerBasicsId] = useState<number | null>(null);
  const [tickerName, setTickerName] = useState<string>(() => {
    const cached = ticker ? sessionStorage.getItem(`ticker-name-${ticker}`) : null;
    return cached || '';
  });

  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 640);
  const [isFullScreen, setIsFullScreen] = useState(false);
  useBodyScrollLock(isFullScreen);
  const [isEditingColumns, setIsEditingColumns] = useState(false);
  const [hiddenStatistics, setHiddenStatistics] = useState<string[]>(() => {
    const key = `stocktable-ticker-proscons-hidden-statistics-${ticker ?? ''}`;
    const saved = sessionStorage.getItem(key);
    try {
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [tempHiddenStatistics, setTempHiddenStatistics] = useState<string[]>([]);

  const pollingStoppedRef = useRef(false);
  const syncPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentAttemptRef = useRef(1);
  const isCheckingSyncRef = useRef(false);
  const tableScrollContainerRef = useRef<HTMLDivElement>(null);

  const api = useMemo(() => (isAuthenticated ? userTickerApi : tickerApi), [isAuthenticated]);

  // Load saved prefs for logged-in users (non-public lists).
  useEffect(() => {
    if (!isAuthenticated) return;
    if (isPublicList) return;
    if (!tickerBasicsId) return;

    loadUserTickerPreferences(tickerBasicsId, 'prosCons')
      .then(({ hiddenStatistics: hiddenFromServer }) => {
        setHiddenStatistics(hiddenFromServer || []);
        try {
          sessionStorage.setItem(`stocktable-ticker-proscons-hidden-statistics-${ticker ?? ''}`, JSON.stringify(hiddenFromServer || []));
        } catch {
          /* ignore */
        }
      })
      .catch(() => {
        // If none exists, fall back to sessionStorage initialization.
      });
  }, [isAuthenticated, isPublicList, tickerBasicsId, ticker]);

  // Resolve ticker_basics_id and market type via getTickerBasics (same pattern as TickerInsiders).
  // Do not read ticker-basics-id from sessionStorage here — it can be stale after DB changes.
  const fetchBasicsId = useCallback(async (symbol: string): Promise<{ id: number; isEtfOrEtv: boolean } | null> => {
    const symbolKey = symbol.toUpperCase();
    const resp = await api.getTickerBasics([symbolKey]);
    if (!resp?.success || !Array.isArray(resp?.data) || resp.data.length === 0) return null;
    const basicsInfo = resp.data.find(
      (t: { tickerSymbol?: string }) => (t.tickerSymbol || '').toUpperCase() === symbolKey,
    );
    const resolvedBasicsId = basicsInfo?.id;
    if (resolvedBasicsId == null) return null;

    sessionStorage.setItem(`ticker-basics-id-${symbol}`, String(resolvedBasicsId));

    const name = basicsInfo.tickerName || symbol;
    const sym = basicsInfo.tickerSymbol || symbol;
    setTickerName(name);
    sessionStorage.setItem(`ticker-name-${symbol}`, name);
    sessionStorage.setItem(`ticker-symbol-${symbol}`, sym);

    return {
      id: resolvedBasicsId,
      isEtfOrEtv: isEtfOrEtvMarketType(basicsInfo.marketType),
    };
  }, [api]);

  const fetchHistory = useCallback(async (id: number) => {
    const resp = await api.getProsAndConsHistory({
      tickerBasicsId: id,
      limit: 6,
      ownerHasProSubscription: isPublicList && publicListData ? publicListData.ownerHasProSubscription : undefined,
    });
    // Nest controllers return { success, message, data: [...] }
    const rows: any[] = resp?.success && Array.isArray(resp?.data) ? resp.data : [];
    const normalized: HistoryItem[] = rows.map((r: any) => ({
      year: r.year ?? null,
      month: r.month ?? null,
      prosCons: r.prosCons ?? null,
    }));
    setHistory(normalized);
  }, [api, isPublicList, publicListData]);

  const checkSync = useCallback(async (id: number, attempt: number) => {
    if (pollingStoppedRef.current || isCheckingSyncRef.current) return;
    isCheckingSyncRef.current = true;
    try {
      const resp = await api.checkProsAndConsSyncStatus({ ticker_basics_ids: [id] } as any);
      const item = resp?.success ? resp?.data?.tickers?.[0] : undefined;
      const waitingForSyncLog = Boolean(effectiveHasPro && !item?.has_sync_log && item?.is_synced === false);
      const syncing = Boolean((item?.is_syncing && !item?.is_synced) || waitingForSyncLog);
      setIsSyncing(syncing);
      if (item?.last_sync_ended_at) {
        const d = new Date(item.last_sync_ended_at);
        if (!isNaN(d.getTime())) setLastUpdated(d);
      }
      // Always refresh history so we show latest available while syncing.
      await fetchHistory(id);
      if (syncing && attempt < 10) {
        if (syncPollingIntervalRef.current) clearInterval(syncPollingIntervalRef.current);
        currentAttemptRef.current = attempt;
        syncPollingIntervalRef.current = setInterval(() => {
          // If a sync check is already in-flight, wait for the next interval tick.
          // (Do not burn attempt counts without making an API call.)
          if (isCheckingSyncRef.current) {
            return;
          }
          const next = currentAttemptRef.current + 1;
          if (next > 10) {
            pollingStoppedRef.current = true;
            if (syncPollingIntervalRef.current) clearInterval(syncPollingIntervalRef.current);
            syncPollingIntervalRef.current = null;
            setIsSyncing(false);
            return;
          }
          currentAttemptRef.current = next;
          checkSync(id, next);
        }, 5000);
      }
    } finally {
      isCheckingSyncRef.current = false;
    }
  }, [api, fetchHistory, effectiveHasPro]);

  useEffect(() => {
    const run = async () => {
      if (!ticker) return;
      setLoading(true);
      setError(null);
      pollingStoppedRef.current = false;
      currentAttemptRef.current = 1;
      try {
        const resolved = await fetchBasicsId(ticker);
        if (!resolved) {
          setError('Ticker not found');
          return;
        }
        setTickerBasicsId(resolved.id);
        if (resolved.isEtfOrEtv) {
          setHistory([]);
          setLastUpdated(null);
          setIsSyncing(false);
          pollingStoppedRef.current = true;
          if (syncPollingIntervalRef.current) {
            clearInterval(syncPollingIntervalRef.current);
            syncPollingIntervalRef.current = null;
          }
          return;
        }
        await checkSync(resolved.id, 1);
      } catch (e: any) {
        setError(e?.message ?? String(e));
      } finally {
        setLoading(false);
      }
    };
    run();
    return () => {
      pollingStoppedRef.current = true;
      if (syncPollingIntervalRef.current) clearInterval(syncPollingIntervalRef.current);
      syncPollingIntervalRef.current = null;
    };
  }, [ticker, fetchBasicsId, checkSync]);

  // Update screen size on window resize
  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ESC to exit fullscreen (align with other Ticker* pages)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullScreen) {
        setIsFullScreen(false);
      }
    };
    if (isFullScreen) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullScreen]);

  const columns = useMemo(() => {
    return history.slice(0, 6);
  }, [history]);

  /** Align with `ListProsCons` / `StockTable` first column width. */
  const indicatorsColumnWidthPx = useMemo(
    () => getProsConsIndicatorsColumnWidthPx({ isSmallScreen, isEditingColumns }),
    [isSmallScreen, isEditingColumns],
  );

  const getHiddenKey = useCallback(() => {
    return `stocktable-ticker-proscons-hidden-statistics-${ticker ?? ''}`;
  }, [ticker]);

  // Edit-mode handlers (sessionStorage-only, like some other ticker pages)
  const handleStartEdit = useCallback(() => {
    setTempHiddenStatistics([...hiddenStatistics]);
    setIsEditingColumns(true);
  }, [hiddenStatistics]);

  const handleCancelEdit = useCallback(() => {
    setTempHiddenStatistics([...hiddenStatistics]);
    setIsEditingColumns(false);
  }, [hiddenStatistics]);

  const handleDoneEdit = useCallback(() => {
    const next = [...tempHiddenStatistics];
    setHiddenStatistics(next);
    try {
      sessionStorage.setItem(getHiddenKey(), JSON.stringify(next));
    } catch {
      /* ignore */
    }
    if (isAuthenticated && !isPublicList && tickerBasicsId) {
      saveUserTickerPreferences(tickerBasicsId, 'prosCons', next).catch(() => {
        // keep UI state; server save failure shouldn't block edit mode
      });
    }
    setIsEditingColumns(false);
  }, [tempHiddenStatistics, getHiddenKey, isAuthenticated, isPublicList, tickerBasicsId]);

  const handleHideStatistic = useCallback((statKey: string) => {
    setTempHiddenStatistics(prev => (prev.includes(statKey) ? prev : [...prev, statKey]));
  }, []);

  const handleShowStatistic = useCallback((statKey: string) => {
    setTempHiddenStatistics(prev => prev.filter(k => k !== statKey));
  }, []);

  const currentHiddenStatistics = useMemo(
    () => (isEditingColumns ? tempHiddenStatistics : hiddenStatistics),
    [isEditingColumns, tempHiddenStatistics, hiddenStatistics],
  );

  const shouldShowRow = useCallback(
    (key: string) => isEditingColumns || !currentHiddenStatistics.includes(key),
    [isEditingColumns, currentHiddenStatistics],
  );

  const renderCell = useCallback((pc: HistoryItem['prosCons'] | null, kind: 'pros' | 'cons') => {
    if (!effectiveHasPro) {
      return (
        <LockIcon
          className="w-4 h-4 text-gray-500 dark:text-gray-400"
          onClick={!isPublicList ? () => navigate('/pricing', { state: { fromUpgrade: true } }) : undefined}
          tooltipText={isPublicList ? "Locked due to the list owner's plan" : 'Upgrade to the Pro Plan to see'}
        />
      );
    }

    // Pro users: while backend reports syncing, show placeholders until sync completes.
    if (isSyncing) {
      return <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-40 rounded" title="Syncing..." />;
    }

    if (!pc) return <span className="text-gray-400 dark:text-text-secondary">-</span>;
    const { pros, cons } = prosConsLineArrays(pc);
    const lines = kind === 'pros' ? pros : cons;
    if (!lines || lines.length === 0) return <span className="text-gray-400 dark:text-text-secondary">-</span>;
    // Match ListProsCons: neutral text color for pros/cons detail.
    return (
      <div className="font-semibold text-text-light-neutral dark:text-text-secondary">
        <ProsConsNumberedList lines={lines} />
      </div>
    );
  }, [effectiveHasPro, isPublicList, navigate, isSyncing]);

  const tabs = (
    <TabBar
      activeTab="proscons"
      disabled={false}
      ticker={ticker}
      publicUuid={publicUuid}
    />
  );

  const renderTable = (opts?: { fullScreen?: boolean }) => {
    const fullScreen = Boolean(opts?.fullScreen);
    const indicatorColWidth = indicatorsColumnWidthPx;
    const monthColWidth = getProsConsHistoryMonthColumnWidthPx({
      isSmallScreen,
      columnCount: columns.length,
      fullScreen,
    });
    const actionColWidth = 60;

    // Pin INDICATORS column width on cells (not only <col>) so sticky + full-width table cannot
    // redistribute slack into column 1 after paint (fixes "narrow flash then wide" column).
    const firstColumnCellStyle: React.CSSProperties = {
      width: indicatorColWidth,
      minWidth: indicatorColWidth,
      maxWidth: indicatorColWidth,
      boxSizing: 'border-box',
    };

    return (
      <div
        className={fullScreen ? 'h-full overflow-auto' : 'w-full overflow-x-auto overflow-y-auto max-h-[80vh]'}
        ref={fullScreen ? undefined : tableScrollContainerRef}
      >
        {/*
          Full-width tables with table-layout:fixed redistribute extra space when column widths
          don't fill 100%. A dedicated spacer column absorbs that slack so INDICATORS stays narrow.
        */}
        <table className="w-full font-mono" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: `${indicatorColWidth}px` }} />
            {columns.map((_, idx) => (
              <col key={idx} style={{ width: `${monthColWidth}px`, minWidth: `${monthColWidth}px` }} />
            ))}
            <col style={{ width: '100%', minWidth: 0 }} />
            <col style={{ width: `${actionColWidth}px`, minWidth: `${actionColWidth}px` }} />
          </colgroup>

          <thead
            className="bg-gray-50 dark:bg-background-card sticky-header-border"
            style={{ position: 'sticky', top: 0, zIndex: 50 }}
          >
            <tr className="h-12 sm:h-16">
              <th
                className={`px-4 text-left align-middle text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider bg-gray-50 dark:bg-background-card`}
                style={{ position: 'sticky', left: 0, zIndex: 40, ...firstColumnCellStyle }}
              >
                <div className="flex items-center">
                  <span>INDICATORS</span>
                </div>
              </th>
              {columns.map((c, idx) => (
                <th
                  key={idx}
                  className={`px-4 text-left align-middle text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider bg-gray-50 dark:bg-background-card`}
                >
                  {formatMonthYear(c.year, c.month)}
                </th>
              ))}
              <th
                className="p-0 border-0 align-middle bg-gray-50 dark:bg-background-card"
                aria-hidden
              />
              <th
                className="px-2 sm:px-4 text-right align-middle bg-gray-50 dark:bg-background-card"
                style={{ position: 'sticky', right: 0, zIndex: 40, width: actionColWidth, minWidth: actionColWidth, maxWidth: actionColWidth, boxSizing: 'border-box' }}
              >
                {!fullScreen ? (
                  <button
                    onClick={() => setIsFullScreen(true)}
                    className="p-1.5 text-gray-500 dark:text-white dark:hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                    title="Open in full screen"
                    type="button"
                    disabled={isEditingColumns}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                      />
                    </svg>
                  </button>
                ) : (
                  <button
                    onClick={() => setIsFullScreen(false)}
                    className="p-1.5 text-gray-500 dark:text-white hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                    title="Close full screen"
                    type="button"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </th>
            </tr>
          </thead>

          <tbody className="bg-surface-light dark:bg-background-card divide-y divide-gray-200 dark:divide-border-color">
            {loading ? (
              <>
                <tr>
                  <td
                    className={`px-4 text-gray-900 dark:text-text-primary font-medium align-top py-3 bg-surface-light dark:bg-background-card ${getTextSizeClass(fontSize)}`}
                    style={{ position: 'sticky', left: 0, zIndex: 30, ...firstColumnCellStyle }}
                  >
                    Pros
                  </td>
                  <td className="px-4 py-3" colSpan={Math.max(1, columns.length)}>
                    <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-40 rounded" />
                  </td>
                  <td className="p-0 border-0 bg-surface-light dark:bg-background-card" aria-hidden />
                  <td className="bg-surface-light dark:bg-background-card" />
                </tr>
                <tr>
                  <td
                    className={`px-4 text-gray-900 dark:text-text-primary font-medium align-top py-3 bg-surface-light dark:bg-background-card ${getTextSizeClass(fontSize)}`}
                    style={{ position: 'sticky', left: 0, zIndex: 30, ...firstColumnCellStyle }}
                  >
                    Cons
                  </td>
                  <td className="px-4 py-3" colSpan={Math.max(1, columns.length)}>
                    <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-40 rounded" />
                  </td>
                  <td className="p-0 border-0 bg-surface-light dark:bg-background-card" aria-hidden />
                  <td className="bg-surface-light dark:bg-background-card" />
                </tr>
              </>
            ) : (
              <>
                {shouldShowRow('pros') && (
                  <tr className={isEditingColumns && currentHiddenStatistics.includes('pros') ? 'opacity-50' : ''}>
                    <td
                      className={`px-4 text-gray-900 dark:text-text-primary font-medium align-top py-3 bg-surface-light dark:bg-background-card ${getTextSizeClass(fontSize)}`}
                      style={{ position: 'sticky', left: 0, zIndex: 30, ...firstColumnCellStyle }}
                    >
                      <div className="flex items-center gap-2">
                        {isEditingColumns && (
                          <button
                            onClick={
                              currentHiddenStatistics.includes('pros')
                                ? () => handleShowStatistic('pros')
                                : () => handleHideStatistic('pros')
                            }
                            className={`transition-all duration-200 p-1 rounded-full active:scale-90 flex-shrink-0 ${
                              currentHiddenStatistics.includes('pros')
                                ? 'text-gray-600 dark:text-text-secondary hover:text-highlight-gold hover:bg-highlight-gold/10'
                                : 'text-gray-600 dark:text-text-secondary hover:text-market-red hover:bg-market-red/10'
                            }`}
                            title={currentHiddenStatistics.includes('pros') ? 'Show statistic' : 'Hide statistic'}
                            type="button"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {currentHiddenStatistics.includes('pros') ? (
                                <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" />
                              ) : (
                                <>
                                  <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" fill="white" className="dark:fill-gray-800" />
                                  <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </>
                              )}
                            </svg>
                          </button>
                        )}
                        <span className="inline-flex items-center gap-1.5">
                          <span className={getStatisticLabelSizeClass(fontSize)}>Pros</span>
                          <span className="px-1.5 py-0.5 text-xs font-semibold text-gray-900 bg-primary/30 dark:bg-highlight-gold/20 dark:text-highlight-gold rounded leading-none">
                            PRO
                          </span>
                        </span>
                      </div>
                    </td>
                    {columns.length === 0 ? (
                      <td className="px-4 py-3 text-gray-400 dark:text-text-secondary">-</td>
                    ) : (
                      columns.map((c, idx) => (
                        <td key={idx} className={`px-4 py-3 align-top font-semibold ${getTextSizeClass(fontSize)} text-text-light-neutral dark:text-text-secondary`}>
                          {renderCell(c.prosCons, 'pros')}
                        </td>
                      ))
                    )}
                    <td className="p-0 border-0 bg-surface-light dark:bg-background-card" aria-hidden />
                    <td className="bg-surface-light dark:bg-background-card" />
                  </tr>
                )}

                {shouldShowRow('cons') && (
                  <tr className={isEditingColumns && currentHiddenStatistics.includes('cons') ? 'opacity-50' : ''}>
                    <td
                      className={`px-4 text-gray-900 dark:text-text-primary font-medium align-top py-3 bg-surface-light dark:bg-background-card ${getTextSizeClass(fontSize)}`}
                      style={{ position: 'sticky', left: 0, zIndex: 30, ...firstColumnCellStyle }}
                    >
                      <div className="flex items-center gap-2">
                        {isEditingColumns && (
                          <button
                            onClick={
                              currentHiddenStatistics.includes('cons')
                                ? () => handleShowStatistic('cons')
                                : () => handleHideStatistic('cons')
                            }
                            className={`transition-all duration-200 p-1 rounded-full active:scale-90 flex-shrink-0 ${
                              currentHiddenStatistics.includes('cons')
                                ? 'text-gray-600 dark:text-text-secondary hover:text-highlight-gold hover:bg-highlight-gold/10'
                                : 'text-gray-600 dark:text-text-secondary hover:text-market-red hover:bg-market-red/10'
                            }`}
                            title={currentHiddenStatistics.includes('cons') ? 'Show statistic' : 'Hide statistic'}
                            type="button"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {currentHiddenStatistics.includes('cons') ? (
                                <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" />
                              ) : (
                                <>
                                  <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" fill="white" className="dark:fill-gray-800" />
                                  <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </>
                              )}
                            </svg>
                          </button>
                        )}
                        <span className="inline-flex items-center gap-1.5">
                          <span className={getStatisticLabelSizeClass(fontSize)}>Cons</span>
                          <span className="px-1.5 py-0.5 text-xs font-semibold text-gray-900 bg-primary/30 dark:bg-highlight-gold/20 dark:text-highlight-gold rounded leading-none">
                            PRO
                          </span>
                        </span>
                      </div>
                    </td>
                    {columns.length === 0 ? (
                      <td className="px-4 py-3 text-gray-400 dark:text-text-secondary">-</td>
                    ) : (
                      columns.map((c, idx) => (
                        <td key={idx} className={`px-4 py-3 align-top font-semibold ${getTextSizeClass(fontSize)} text-text-light-neutral dark:text-text-secondary`}>
                          {renderCell(c.prosCons, 'cons')}
                        </td>
                      ))
                    )}
                    <td className="p-0 border-0 bg-surface-light dark:bg-background-card" aria-hidden />
                    <td className="bg-surface-light dark:bg-background-card" />
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  if (error && !loading && history.length === 0) {
    return (
      <div className="flex flex-col min-h-screen bg-background-light dark:bg-background">
        <Header sticky={false} />
        {publicUuid && !isAuthenticated && <PublicListPromoBar />}
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-2">Error</h2>
              <p className="text-gray-600 dark:text-text-secondary">{error}</p>
            </div>
          </div>
        </main>
        <div className="mt-4 sm:mt-8">
          <Footer />
        </div>
      </div>
    );
  }

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        table thead.sticky-header-border::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background-color: rgb(229, 231, 235);
          z-index: 51;
          pointer-events: none;
        }
        .dark table thead.sticky-header-border::after {
          background-color: rgb(55, 65, 81);
        }
      `,
        }}
      />
      <div className="flex flex-col min-h-screen bg-background-light dark:bg-background">
        {!isFullScreen && <Header sticky={false} />}
        {!isFullScreen && publicUuid && !isAuthenticated && <PublicListPromoBar />}

        <main className="px-1 lg:px-2 xl:px-4 pt-4 sm:pt-8 pb-4 sm:pb-8">
          <div className="bg-surface-light dark:bg-background-card border border-border-light dark:border-border-color rounded-md shadow-lg overflow-hidden">
            {/* Back button */}
            <div className="p-4">
              <button
                onClick={() => {
                  if (publicUuid) navigate(`/proscons?public_uuid=${publicUuid}`);
                  else navigate('/proscons');
                }}
                className={`flex items-center ${getTextSizeClass(fontSize)} text-text-light-neutral dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors`}
                type="button"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to List
              </button>
            </div>

            {/* Header */}
            <div className="px-4 pb-4">
              <div className="flex items-center gap-4">
                <h1 className="text-xl font-semibold text-text-light-primary dark:text-text-dark-primary">
                  {tickerName || ticker?.toUpperCase()} ({ticker?.toUpperCase()})
                </h1>
                {!isPublicList && !isEditingColumns ? (
                  <button
                    onClick={handleStartEdit}
                    className="transition-colors duration-200 text-gray-600 dark:text-text-secondary hover:text-gold-text-light dark:hover:text-highlight-gold"
                    title="Edit indicators"
                    type="button"
                  >
                    <svg className="lucide lucide-pencil" fill="none" height="20" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"></path>
                      <path d="m15 5 4 4"></path>
                    </svg>
                  </button>
                ) : null}
                {!isPublicList && isEditingColumns ? (
                  <div className="flex items-center gap-4">
                    <button
                      onClick={handleCancelEdit}
                      className="text-text-light-neutral dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors duration-200 text-sm font-medium"
                      type="button"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDoneEdit}
                      className="bg-primary text-gray-900 px-3 py-1.5 rounded-md text-sm font-medium hover:bg-opacity-90 transition-colors duration-200"
                      type="button"
                    >
                      Done
                    </button>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Navigation Tabs (match TickerInsiders layout) */}
            <nav className="border-b border-gray-200 dark:border-border-color">
              <div className="px-4">
                {tabs}
              </div>
            </nav>

            {/* Table (full width, flush under tabs) */}
            {renderTable()}

            {/* Status Bar (match TickerInsiders) */}
            <div
              className={`px-4 py-2 border-t border-gray-200 dark:border-border-color bg-gray-50 dark:bg-black/10 flex justify-between items-center ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary`}
            >
              <div className="flex items-center gap-4">
                {loading && (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin w-3 h-3 border border-highlight-gold border-t-transparent rounded-full" />
                    <span>Loading...</span>
                  </div>
                )}
                {lastUpdated && !loading && (
                  <span>
                    Last updated: {lastUpdated.toLocaleDateString()} {lastUpdated.toLocaleTimeString()}
                  </span>
                )}
              </div>
            </div>
          </div>
        </main>

        {/* Full Screen Overlay */}
        {isFullScreen && (
          <div
            className="fixed inset-0 z-[200] flex min-h-0 flex-col bg-background-light dark:bg-background"
            role="dialog"
            aria-modal="true"
          >
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain">
              {renderTable({ fullScreen: true })}
            </div>
          </div>
        )}

        <div className="mt-4 sm:mt-8">
          <Footer />
        </div>
      </div>
    </>
  );
};

export default TickerProsCons;

